<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Regulation to Practice: Quality Systems — QikSolve',
  description:
    'How regulated organisations turn GMP, GxP, 21 CFR Part 11, and Annex 11 expectations into workable quality practice on Microsoft 365.',
});

const page = {
  hero: {
    eyebrow: 'Regulation to practice',
    title: 'From Regulatory Expectation to Workable Quality Practice',
    summary:
      'How do you use Microsoft 365 to turn abstract regulatory expectations into daily practice that your team can execute, evidence, and sustain?',
    cta: { label: 'Book a discovery call', to: '/contact' },
  },
  intro: {
    title: 'What regulation to practice means',
    content:
      "Regulations like GMP, GxP, FDA 21 CFR Part 11, EU Annex 11, and ISO standards define what compliance means. They do not tell you how your people, processes, and systems should work together. This page explores that gap and how Microsoft 365, with the right approach, may support both the practice of quality and the evidence that control is embedded.",
    items: [
      {
        title: 'Interpretation',
        description: 'What does the regulation require for your operation, technology choices, and intended use?',
      },
      {
        title: 'Ownership',
        description: 'Who owns each decision, record, and quality outcome, and how do they stay accountable?',
      },
      {
        title: 'Process design',
        description: 'How should people, workflows, and approvals work so good practice becomes the default?',
      },
      {
        title: 'Traceability',
        description: 'How do you record decisions, changes, evidence, and review in a usable way?',
      },
      {
        title: 'Continuous evidence',
        description: 'How do you demonstrate that the system remains controlled and practice remains embedded?',
      },
    ],
  },
  sections: [
    {
      title: 'What your organisation needs to assess',
      items: [
        {
          title: 'Regulatory scope',
          description: 'Identify which regulations apply and which requirements affect workflows, records, changes, and evidence.',
        },
        {
          title: 'Intended use and system boundary',
          description: 'Define the processes and data that will run on Microsoft 365 and how critical they are.',
        },
        {
          title: 'Current practice and control gaps',
          description: 'Understand how quality decisions are made and evidenced today, and where control gaps exist.',
        },
        {
          title: 'Ownership and accountability',
          description: 'Clarify who owns design, operation, change control, evidence, and regulatory readiness.',
        },
        {
          title: 'Evidence and documentation strategy',
          description: 'Define what evidence must be produced, retained, retrieved, and reviewed.',
        },
      ],
    },
    {
      title: 'Where Microsoft 365 may support the operating model',
      content:
        'Microsoft 365, including SharePoint, Teams, Power Automate, Excel, and related services, can be configured to support many aspects of regulated quality practice. The platform must be shaped by the practice your organisation needs to sustain.',
      items: [
        {
          title: 'Controlled records',
          description: 'Versioning, approval workflows, retention, and access control for quality documentation.',
        },
        {
          title: 'Traceable decisions',
          description: 'Structured workflows with audit trails, approvals, comments, and change history.',
        },
        {
          title: 'Role-based accountability',
          description: 'Clear assignment of who can do what through identity and access controls.',
        },
        {
          title: 'Audit-friendly practices',
          description: 'Logging of user actions, system changes, and evidence preservation for investigation and review.',
        },
        {
          title: 'Continuous transparency',
          description: 'Dashboards, quality metrics, and operational insights that show how control operates day to day.',
        },
      ],
    },
    {
      title: 'Validation, evidence, and change control',
      content:
        'Validation is often treated as a one-time milestone, but regulated operations require ongoing assessment. Every configuration change, update, or workflow modification requires documented assessment to confirm that control is not undermined. Evidence should be generated through operations rather than reconstructed retrospectively.',
      items: [
        {
          title: 'Validation is continuous',
          description: 'Assess changes and modifications throughout the system lifecycle, not only before deployment.',
        },
        {
          title: 'Evidence is embedded',
          description: 'Design logs, audit trails, approvals, and change management into the operating model from the start.',
        },
      ],
    },
    {
      title: "QikSolve's role: practical partnership",
      content:
        'QikSolve supports regulated organisations in translating regulatory expectations and current industry practice into workable, controlled quality systems on Microsoft 365. We are not auditors or certification bodies. We do not guarantee compliance, audit success, or regulatory approval.',
      items: [
        {
          title: 'Clarify the context',
          description: 'Interpret applicable regulations and translate them into specific, testable requirements.',
        },
        {
          title: 'Assess current state',
          description: 'Identify control gaps, ownership ambiguities, and operating-model misalignments.',
        },
        {
          title: 'Design the operating model',
          description: 'Structure decisions, approvals, records, and evidence so practice is embedded and reviewable.',
        },
        {
          title: 'Plan configuration and validation',
          description: 'Map the operating model to Microsoft 365 capabilities and plan appropriate evidence and testing.',
        },
        {
          title: 'Prepare for ongoing control',
          description: 'Establish processes for evidence capture, safe configuration change, and regulatory review readiness.',
        },
      ],
    },
  ],
  callout: {
    title: 'A practical operating model matters more than paperwork alone',
    content:
      'The most effective quality systems are built around the work people actually do: ownership, review, evidence, and decisions that are clear, repeatable, and sustainable.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Related practice areas',
    items: [
      {
        label: 'Governed SharePoint practice',
        description: 'How a governed Microsoft 365 environment supports compliance day to day.',
        to: '/product/sharepoint-governance',
      },
      {
        label: 'ISO 9001 in practice',
        description: 'Translating ISO 9001 requirements into embedded, everyday quality practice.',
        to: '/product/iso-9001',
      },
      {
        label: 'Quality competency and training',
        description: 'The ownership and competency evidence needed to sustain practice.',
        to: '/product/training',
      },
      {
        label: 'QikSolve services and pathways',
        description: 'The full range of QikSolve services and product referrals.',
        to: '/product',
      },
      {
        label: 'GxP compliance with Azure',
        description: 'Read the existing overview of Azure, Part 11, and Annex 11 considerations.',
        to: '/blog/achieving-gxp-compliance-with-azure-cfr-21-part-11-and-annex-11-made-simple',
      },
      {
        label: 'Is SharePoint GMP compliant?',
        description: 'Explore the existing article on SharePoint and GMP quality-system context.',
        to: '/blog/gmp-compliance',
      },
      {
        label: 'CAPA management and Part 11',
        description: 'Read the existing CAPA article for related electronic-record context.',
        to: '/blog/gmp-capa-management-cfr-21-part-11',
      },
    ],
  },
  cta: {
    title: 'Ready to explore your path forward?',
    content:
      'Bring your regulatory context, operating-model questions, and evidence needs to a conversation about a sustainable quality pathway.',
    label: 'Book a discovery call',
    to: '/contact',
  },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
